import { ChevronLeft, ChevronRight, CopyPlus, Plus, Trash2 } from 'lucide-react';
import { orgYear } from '@/lib/today';
import * as React from 'react';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Checkbox, Combobox,
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label,
  Skeleton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/cargofl-ui';
import { ApiError, toastFailure } from '@/lib/api';
import { ConfirmButton } from '@/components/confirm-dialog';
import {
  useCopyHolidayYear, useCreateHoliday, useDeleteHoliday, useHolidayGroups,
  useHolidays, useUpdateHoliday, type Holiday,
} from '@/lib/admin-api';

/**
 * The public holiday calendar.
 *
 * This is load-bearing and time-limited: a mandatory holiday removes the day's
 * target hours for everybody on the calendar, and the daily fill job books an entry
 * for it. A year with no holidays entered is a year where every one of those days
 * reads as a shortfall, so the panel leads with a warning when the current or next
 * year is empty rather than waiting for somebody to notice in January.
 */
export function HolidaysPanel() {
  const thisYear = orgYear();
  const { data: groups, isPending: groupsPending } = useHolidayGroups();
  const [groupId, setGroupId] = React.useState<string | undefined>(undefined);
  const [year, setYear] = React.useState(thisYear);

  const effectiveGroup = groupId ?? groups?.[0]?.id;
  const { data: holidays, isPending } = useHolidays(effectiveGroup, year);
  const { data: nextYear } = useHolidays(effectiveGroup, thisYear + 1);

  const remove = useDeleteHoliday();
  const update = useUpdateHoliday();
  const [adding, setAdding] = React.useState(false);
  const [copying, setCopying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  if (groupsPending) return <Skeleton className="h-64 w-full" />;

  const rows = holidays ?? [];
  const nextYearEmpty = (nextYear?.length ?? 0) === 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[220px]">
          <Combobox
            value={effectiveGroup ?? null}
            onChange={setGroupId}
            options={(groups ?? []).map((g) => ({
              value: g.id,
              label: g.name,
              hint: `${g._count.contracts} people`,
            }))}
            placeholder="Choose a calendar"
            searchPlaceholder="Search calendars"
          />
        </div>
        <Button variant="outline" size="icon" aria-label="Previous year"
          onClick={() => setYear((y) => y - 1)}>
          <ChevronLeft className="size-4" />
        </Button>
        <span className="ts-num min-w-[3.5rem] text-center text-sm font-semibold">{year}</span>
        <Button variant="outline" size="icon" aria-label="Next year"
          onClick={() => setYear((y) => y + 1)}>
          <ChevronRight className="size-4" />
        </Button>
        <div className="ml-auto flex gap-2">
          <Button hint={"Copy one year's holidays into another year"} variant="outline" onClick={() => setCopying(true)}>
            <CopyPlus className="size-4" />
            Copy a year
          </Button>
          <Button hint={"Add a public holiday — nobody is expected to book time on it"} onClick={() => setAdding(true)} disabled={!effectiveGroup}>
            <Plus className="size-4" />
            Add a holiday
          </Button>
        </div>
      </div>

      {/* Surfaced here rather than left to be discovered: the fill job silently has
          nothing to book, and every affected day reads as a shortfall. */}
      {nextYearEmpty && (
        <Alert className="border-warning/40">
          <AlertDescription className="text-sm leading-relaxed">
            No holidays are entered for <strong>{thisYear + 1}</strong>. From 1 January
            the daily fill job will have nothing to book, and every holiday that year
            will read as a shortfall on people&rsquo;s timesheets. Copy {thisYear}{' '}
            forward as a starting point, then correct the dates.
          </AlertDescription>
        </Alert>
      )}

      {!effectiveGroup && groups && (
        <p className="text-sm text-muted-foreground">
          There is no holiday group yet, so there is nowhere to add a holiday.
        </p>
      )}
      {error && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert>
          <AlertDescription className="text-sm leading-relaxed">{notice}</AlertDescription>
        </Alert>
      )}

      {isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table className="min-w-[620px]">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Holiday</TableHead>
                    <TableHead>Counts as</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((h) => (
                    <HolidayRow
                      key={h.id}
                      holiday={h}
                      busy={update.isPending || remove.isPending}
                      onToggleMandatory={() => {
                        setError(null);
                        update.mutate(
                          { id: h.id, mandatory: !h.mandatory },
                          {
                            onError: (e) => {
                              setError(`"${h.name}": ${msg(e)}`);
                              toastFailure(`Could not change "${h.name}"`)(e);
                            },
                          },
                        );
                      }}
                      onDelete={() => {
                        setError(null);
                        setNotice(null);
                        remove.mutate(h.id, {
                          onSuccess: (r) => {
                            const left = (r as { entriesLeftInPlace?: number })
                              ?.entriesLeftInPlace ?? 0;
                            if (left > 0) {
                              // Not cleaned up silently: those are hours people were
                              // credited, in months that may already be approved.
                              setNotice(
                                `Removed "${h.name}". ${left} timesheet entries already booked for it were left in place — withdrawing them would change months that may already be approved.`,
                              );
                            }
                          },
                          onError: (e) => {
                            setError(`Could not remove "${h.name}": ${msg(e)}`);
                            toastFailure(`Could not remove "${h.name}"`)(e);
                          },
                        });
                      }}
                    />
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                        No holidays entered for {year}.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {adding && effectiveGroup && (
        <AddHolidayDialog
          groupId={effectiveGroup}
          defaultYear={year}
          onClose={() => setAdding(false)}
        />
      )}
      {copying && effectiveGroup && (
        <CopyYearDialog
          groupId={effectiveGroup}
          thisYear={thisYear}
          onClose={() => setCopying(false)}
          onDone={(text) => {
            setNotice(text);
            setCopying(false);
          }}
        />
      )}
    </div>
  );
}

function HolidayRow({
  holiday,
  busy,
  onToggleMandatory,
  onDelete,
}: {
  holiday: Holiday;
  busy: boolean;
  onToggleMandatory: () => void;
  onDelete: () => void;
}) {
  const date = new Date(holiday.date);
  return (
    <TableRow>
      <TableCell className="ts-num whitespace-nowrap">
        {date.toLocaleDateString('en-IN', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          timeZone: 'UTC',
        })}
      </TableCell>
      <TableCell className="font-medium">{holiday.name}</TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-2">
          {holiday.mandatory ? (
            <Badge>a day off for everyone</Badge>
          ) : (
            <Badge variant="outline">optional — a normal working day</Badge>
          )}
          {holiday.halfDay && <Badge variant="secondary">half day</Badge>}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-3">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox
              checked={holiday.mandatory}
              disabled={busy}
              onCheckedChange={onToggleMandatory}
              aria-label="Mandatory"
            />
            mandatory
          </label>
          <ConfirmButton
            variant="ghost"
            size="icon"
            aria-label="Remove holiday"
            disabled={busy}
            pending={busy}
            title={`Remove "${holiday.name}"?`}
            description={
              <>
                {/* The consequence people do not expect: the nightly fill has
                    already booked this into timesheets, in months that may be
                    approved and locked. Removing the holiday leaves those hours. */}
                Everybody in this group will be expected to work {holiday.date}. Any hours
                already booked against it stay where they are &mdash; including in months
                that are already approved &mdash; so those will need correcting by hand.
              </>
            }
            confirmLabel="Remove holiday"
            destructive
            onConfirm={onDelete}
          >
            <Trash2 className="size-4" />
          </ConfirmButton>
        </div>
      </TableCell>
    </TableRow>
  );
}

function AddHolidayDialog({
  groupId,
  defaultYear,
  onClose,
}: {
  groupId: string;
  defaultYear: number;
  onClose: () => void;
}) {
  const create = useCreateHoliday();
  const [date, setDate] = React.useState(`${defaultYear}-01-01`);
  const [name, setName] = React.useState('');
  const [mandatory, setMandatory] = React.useState(true);
  const [halfDay, setHalfDay] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ groupId, date, name, mandatory, halfDay });
      onClose();
    } catch (err) {
      setError(msg(err));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a holiday</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="h-date">Date</Label>
              <Input id="h-date" type="date" value={date}
                onChange={(e) => setDate(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="h-name">Name</Label>
              <Input id="h-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Diwali" required />
            </div>
          </div>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox checked={mandatory} onCheckedChange={(v) => setMandatory(v === true)} />
            <span>
              A day off for everyone
              <span className="block text-xs text-muted-foreground">
                Removes the day&rsquo;s target hours and is booked automatically. Uncheck
                for an optional holiday people may take as leave instead.
              </span>
            </span>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={halfDay} onCheckedChange={(v) => setHalfDay(v === true)} />
            Half day only
          </label>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add holiday'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CopyYearDialog({
  groupId,
  thisYear,
  onClose,
  onDone,
}: {
  groupId: string;
  thisYear: number;
  onClose: () => void;
  onDone: (notice: string) => void;
}) {
  const copy = useCopyHolidayYear();
  const [from, setFrom] = React.useState(String(thisYear));
  const [to, setTo] = React.useState(String(thisYear + 1));
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const result = await copy.mutateAsync({
        groupId,
        fromYear: Number(from),
        toYear: Number(to),
      });
      onDone(
        `Copied ${result.created} holiday(s) into ${to}${result.skipped ? `, skipping ${result.skipped} already there` : ''}. ${result.note}`,
      );
    } catch (err) {
      setError(msg(err));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Copy a year forward</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="from-year">From</Label>
              <Input id="from-year" className="ts-num" value={from}
                onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="to-year">To</Label>
              <Input id="to-year" className="ts-num" value={to}
                onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <Alert>
            <AlertDescription className="text-sm leading-relaxed">
              Dates are copied <strong>as they are</strong>. Most of these holidays move
              each year with the lunar calendar, so treat this as a starting point and
              check every date afterwards. Anything already entered is left alone.
            </AlertDescription>
          </Alert>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button hint={"Copy these holidays into the chosen year"} type="submit" disabled={copy.isPending}>
              {copy.isPending ? 'Copying…' : 'Copy'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function msg(error: unknown): string {
  return error instanceof ApiError ? error.message : 'That did not work.';
}
